import {
  incompleteClaimFields,
  withIncompleteClaimValidation,
} from './incompleteClaim';
import { z } from 'zod';
import { healthcardSchema } from './healthcard';
import { patientSchema } from './patient';
import { payorSchema } from './payor';
import { providerSchema } from './provider';
import { siteSchema } from './site';
import { submissionSchema } from './submission';
import {
  tariffSchemaWithOptionalICD9,
  tariffSchemaWithRequiredICD9,
} from './tariff';
import { debitSchema } from './debit';
import { baseClaimSchema } from './baseClaim';
import { remittanceSchema } from './remittance';
import { statementSchema } from './statement';
import { invoiceSchema } from './invoice';
import { groupSchema } from './group';
import { baseUserSchema } from './user';
import { claimQuerySchema } from './claimQuery';
import { claimPaymentAttachmentSchema } from './attachment';
import { receiptPaymentMethods } from './receipt';
import { validateClaimLocationOfService } from './claimLocationOfService';
import { validateProvinceClaimFields } from './provinceClaimFields';

export const claimSchema = baseClaimSchema
  .extend({
    healthcard: z.lazy(() => healthcardSchema).nullable(),
    site: z.lazy(() => siteSchema),
    patient: z.lazy(() => patientSchema).nullable(),
    provider: z.lazy(() => providerSchema).nullable(),
    payor: z.lazy(() => payorSchema).nullable(),
    debits: z.lazy(() => debitSchema.array()).nullable(),
    claimHistories: z.array(z.unknown()),
    statements: z.lazy(() => statementSchema.array()),
    tariffs: z.lazy(() =>
      tariffSchemaWithOptionalICD9
        .array()
        .min(1, 'Must have at least one tariff')
    ),
    submissions: z.lazy(() => submissionSchema.array()),
    remittances: z.lazy(() => remittanceSchema.array()).optional(),
    invoices: z.lazy(() => invoiceSchema.array()).optional(),
    group: z
      .lazy(() => groupSchema)
      .optional()
      .nullable(),
    createdByUser: z
      .lazy(() => baseUserSchema)
      .optional()
      .nullable(),
    claimQueries: z.lazy(() => claimQuerySchema.array()).optional(),
    hasQueryResponse: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    const requiresTariffIcd9 = data.site?.requiresTariffIcd9 ?? true;
    if (!requiresTariffIcd9) {
      return;
    }

    data.tariffs.forEach((tariff, index) => {
      const parsedTariff = tariffSchemaWithRequiredICD9.safeParse(tariff);
      if (parsedTariff.success) {
        return;
      }

      parsedTariff.error.issues
        .filter((issue) => issue.path[0] === 'icd9')
        .forEach((issue) => {
          ctx.addIssue({
            code: 'custom',
            message: issue.message,
            path: ['tariffs', index, ...issue.path],
          });
        });
    });
  })
  .superRefine(validateClaimLocationOfService)
  .superRefine(validateProvinceClaimFields);

const moneySchema = z
  .string()
  .regex(/^\d+(?:\.\d{1,2})?$/, 'Expected a non-negative money value');

export const recordClaimPaymentSchema = z
  .object({
    claimID: z.guid(),
    creditID: z.guid(),
    paymentDate: z.iso.datetime({ offset: true }),
    paymentMethod: z.enum(Object.values(receiptPaymentMethods)),
    comment: z.string(),
    perTariffAmounts: z.record(z.guid(), moneySchema),
    invoiceID: z.guid().nullable().optional(),
    attachments: z.array(claimPaymentAttachmentSchema).max(5),
  })
  .strict();

export const recordClaimPaymentResultSchema = z
  .object({
    creditID: z.guid(),
    claimID: z.guid(),
    attachmentIDs: z.array(z.guid()),
    receiptID: z.guid().nullable(),
    receiptGenerated: z.boolean(),
    warnings: z.array(z.string()),
  })
  .strict();

export type RecordClaimPaymentInput = z.infer<typeof recordClaimPaymentSchema>;
export type RecordClaimPaymentResult = z.infer<
  typeof recordClaimPaymentResultSchema
>;

export const savedClaimSchema = withIncompleteClaimValidation(
  claimSchema,
  z.object(claimSchema.shape).extend(incompleteClaimFields)
);
